import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Shuffle, Settings, ShoppingBag, List, Calendar as CalendarIcon, Info, Check, X, Trash } from 'lucide-react';
import AddMealModal from './AddMealModal';
import TemplateBuilderModal from './TemplateBuilderModal';
import MarketListModal from './MarketListModal';
import { useStore, Recipe, DayPlan, Meal } from '../../store/useStore';
import { getWeekInfo, getWeekIdFromDate, getWeekInfoFromDate, getDateRangeFromWeekId, createEmptyWeek, normalizeWeek } from '../../lib/weekHelper';
import Alert from '../ui/custom/Alert';
import { formatDate } from '@/lib/dateFormatter';
import RecipeModal from '../cookbook/RecipeModal';
import { useConfirm } from '../ui/custom/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const MealPlanner: React.FC = () => {
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [showAddMealModal, setShowAddMealModal] = useState<boolean>(false);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const [showMarketList, setShowMarketList] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [mealPlan, setMealPlan] = useState<Recipe[]>([]);
  const [selectedMealType, setSelectedMealType] = useState<string>('');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const mealTemplate = useStore((state) => state.mealTemplate);
  const globalWeeklyPlan = useStore((state) => state.weeklyPlan);
  const setGlobalPlans = useStore((state) => state.setWeeklyPlan);
  const [isLoading, setIsLoading] = useState(true);
  const [currentWeekId, setCurrentWeekId] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const generatingWeeklyPlan = useStore((state) => state.generatingWeeklyPlan);
  const clearPlan = useStore((state) => state.clearPlan);
  const user = useStore((state) => state.user);
  const recipes = useStore((state) => state.recipes);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState<Meal[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
  const [allPlans, setAllPlans] = useState<Record<string, DayPlan[]>>(globalWeeklyPlan);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);


  const confirm = useConfirm();
  const { toast } = useToast();

  const [weeklyPlan, setWeeklyPlan] = useState<DayPlan[]>(() => createEmptyWeek());

  const removeMealFromPlan = useStore((state) => state.removeMealFromPlan);
  const addMealToPlan = useStore((state) => state.addMealToPlan);
  const [theme, setTheme] = useStore((state) => [
    state.userPreferences?.theme || 'dark',
    state.updatePreferences
  ]);

  useEffect(() => {
    setIsGeneratingPlan(generatingWeeklyPlan);
    setIsLoading(generatingWeeklyPlan);
  }, [generatingWeeklyPlan]);

  useEffect(() => {
    const query = "(max-width: 1023px)";
    const mql = window.matchMedia(query);

    // set initial state
    setIsTabletOrMobile(mql.matches);
    if (mql.matches) setViewMode('list')

    // listener that fires whenever width crosses the breakpoint
    const handler = (e: any) => {
      setIsTabletOrMobile(e.matches);
      if (e.matches) setViewMode('list')
    }

    // add the correct event depending on browser
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
    } else {
      // Safari <14
      mql.addListener(handler);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", handler);
      } else {
        mql.removeListener(handler);
      }
    };
  }, []);


  useEffect(() => {
    if (!user?.id) return;
    const ref = doc(db, "weeklyPlans", user.id);
    const unsubscribe = onSnapshot(ref, snap => {
      if (!snap.exists()) { setIsLoading(false); return; }

      const data = snap.data() as Record<string, DayPlan[]>;

      // format every week once
      const formatted = Object.fromEntries(
        Object.entries(data).map(([weekId, plans]) => [
          weekId,
          plans
            .map(p => ({ ...p, date: formatDate(p.date) }))
            .sort((a, b) => {
              const aTime = a?.date ? a.date.getTime() : 0;
              const bTime = b?.date ? b.date.getTime() : 0;
              return aTime - bTime;
            }),
        ])
      );

      setGlobalPlans(formatted);
      setAllPlans(formatted);

      // also set the current week’s plan immediately

      const wkId = currentWeekId || getWeekInfoFromDate(new Date());
      setCurrentWeekId(wkId);
      if (formatted[wkId]) setWeeklyPlan(normalizeWeek(formatted[wkId], wkId));

      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);


  // Theme handling
  useEffect(() => {
    if (!theme) {
      const newTheme = localStorage.getItem('theme') || 'dark';
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme({ theme: newTheme });
      }
    }
  }, [theme]);


  const handleRemoveMeal = async (date: Date, mealType: string, mealTitle?: string) => {
    const ok = await confirm({
      title: "Remove meal?",
      description: `Are you sure you want to remove "${mealTitle || mealType}" from ${date.toDateString()}? This action cannot be undone.`,
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
    });

    if (!ok) return;

    try {
      await removeMealFromPlan(date, mealType, currentWeekId);
      toast({
        title: "Meal removed",
        description: `${mealType} removed from ${date.toDateString()} successfully.`,
      });
    } catch {
      toast({
        title: "Failed to remove meal",
        description: "Something went wrong while removing the meal.",
        variant: "destructive",
      });
    }
  };



  const areAllMealsSet = (weekPlan: DayPlan[]) => {
    return weekPlan.every(day => {
      const meals = day.meals;

      // If there are no keys in the meals object, it's undefined
      if (!meals || Object.keys(meals).length === 0) return true;

      // All meal types (e.g. breakfast, lunch, snacks, etc.) must be empty arrays
      return Object.values(meals).every(mealArray =>
        Array.isArray(mealArray) && mealArray.length === 0
      );
    });
  }

  // useEffect(() => {
  //   const weekId = getWeekInfo();
  //   const weekPlan = currentWeekId ? allPlans?.[currentWeekId] : allPlans?.[weekId];
  //   if (!currentWeekId) {
  //     setCurrentWeekId(weekId);
  //   }
  //   console.log('Meal Plan added:', weekId, mealPlan, weekPlan, getWeekIdFromDate(new Date()));

  //   if (weekPlan?.length > 0) {
  //     const formattedWeekPlan = weekPlan
  //       .map((item: DayPlan) => ({
  //         ...item,
  //         date: formatDate(item.date),
  //       }))
  //       .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
  //     setWeeklyPlan(formattedWeekPlan);
  //   }

  //   setIsLoading(false);
  // }, [allPlans]);

  // useEffect(() => {
  //   // Check if the weekly plan exists in the store
  //   // If it does, set it to the local state
  //   const weekId = getWeekInfo();
  //   const weekPlan = currentWeekId ? allPlans?.[currentWeekId] : allPlans?.[weekId];
  //   if (!currentWeekId) {
  //     setCurrentWeekId(weekId);
  //   }
  //   setCurrentWeekId(weekId);
  //   console.log('Meal Plan added:', weekId, mealPlan, weekPlan, getWeekIdFromDate(new Date()));
  //   if (weekPlan) {
  //     if (weekPlan.length > 0) {
  //       const formattedWeekPlan = weekPlan
  //         .map((item: DayPlan) => ({
  //           ...item,
  //           date: formatDate(item.date),
  //         }))
  //         .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
  //       setWeeklyPlan(formattedWeekPlan);
  //     }
  //   }
  //   setIsLoading(false);
  // }, [mealPlan, mealTemplate]);

  // useEffect(() => {
  //   // Check if the weekly plan exists in the store
  //   // If it does, set it to the local state
  //   const weekId = getWeekInfo();
  //   const weekPlan = currentWeekId ? allPlans?.[currentWeekId] : allPlans?.[weekId];
  //   if (!currentWeekId) {
  //     setCurrentWeekId(weekId);
  //   }
  //   console.log('All Weekly Plans 1:', allPlans);
  //   if (weekPlan) {
  //     if (weekPlan.length > 0) {
  //       const formattedWeekPlan = weekPlan
  //         .map((item: DayPlan) => ({
  //           ...item,
  //           date: formatDate(item.date),
  //         }))
  //         .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
  //       setWeeklyPlan(formattedWeekPlan);
  //     }
  //   }
  //   setIsLoading(false);
  // }, []);

  const addToMealTemplate = () => {
    // Logic to add a recipe to the meal template
    console.log('Adding to meal template...');
  }

  const navigateWeek = (direction: "prev" | "next") => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + (direction === "prev" ? -7 : 7));

    const newWeekId = getWeekInfoFromDate(newDate);

    const weekPlan = allPlans[newWeekId];
    if (weekPlan) {
      setCurrentWeekId(newWeekId);
      setCurrentWeek(newDate);
      console.log("[SETTING PLAN:] ", newWeekId, weekPlan, normalizeWeek(weekPlan, newWeekId), createEmptyWeek());
      setWeeklyPlan(normalizeWeek(weekPlan, newWeekId));
    }
    // else {
    //   setWeeklyPlan(createEmptyWeek()); // or generate defaults
    // }

  };


  const formatDate_ = (date: Date) => {
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDayName = (date: Date) => {
    return date?.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const openAddMealModal = (day: Date, mealType: string, meals?: Meal[]) => {
    setSelectedDay(day);
    setSelectedMealType(mealType);
    setSelectedMeals(meals ?? []);
    setShowAddMealModal(true);
  };

  const handleAddMeal = (recipe: Recipe[]) => {
    if (!selectedDay || !selectedMealType) return;

    const newMeal: Meal[] = recipe.map(item => ({
      id: Math.random().toString(36).substr(2, 9),
      title: item.title,
      type: selectedMealType as 'breakfast' | 'lunch' | 'dinner' | 'snack',
      image: item.image,
      calories: Number(item.calories) || 0,
      recipeId: item.id
    }));

    addMealToPlan(selectedDay, selectedMealType as 'breakfast' | 'lunch' | 'dinner' | 'snack', currentWeekId, newMeal);

    setWeeklyPlan(prevPlan =>
      prevPlan.map(dayPlan => {
        if (dayPlan?.date?.getTime() === selectedDay.getTime()) {
          return {
            ...dayPlan,
            meals: {
              ...dayPlan.meals,
              [selectedMealType]: newMeal
            }
          };
        }
        return dayPlan;
      })
    );
  };

  const handleGenerateWeeklyPlan = async () => {
    setAlertIsOpen(true);
  }

  const handleClearPlan = async () => {
    const ok = await confirm({
      title: "Clear Plan?",
      description: `Are you sure you want to clear your plan? This action cannot be undone.`,
      confirmLabel: "Clear Plan",
      cancelLabel: "Cancel",
    });

    if (!ok) return;
    clearPlan();
  }

  const calculateDailyCalories = (day: DayPlan): number => {
    let total = 0;
    let breakfast = Array.isArray(day.meals.breakfast) ? day.meals.breakfast : [day.meals.breakfast];
    let lunch = Array.isArray(day.meals.lunch) ? day.meals.lunch : [day.meals.lunch];
    let dinner = Array.isArray(day.meals.dinner) ? day.meals.dinner : [day.meals.dinner];
    if (breakfast) {
      total += breakfast.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
    }
    if (lunch) {
      total += lunch.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
    }
    if (dinner) {
      total += dinner.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
    }
    // day.meals.snacks.forEach(snack => total += snack.calories);
    return total;
  };

  const handleShowRecipe = (meal: Meal) => {
    const recipe = recipes.find(recipe => recipe.id == meal.recipeId);
    setSelectedRecipe(recipe);
  }

  const renderCalendarView = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-x-auto shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="min-w-[800px]">
        <div className="grid grid-cols-8 divide-x divide-gray-200 dark:divide-gray-700 text-center">
          <div className="py-3 px-4 bg-gray-50 dark:bg-gray-900">
            <span className="text-gray-500 dark:text-gray-400 font-medium">Meal</span>
          </div>

          {weeklyPlan.map((dayPlan, index) => (
            <div
              key={index}
              className={`py-3 px-1 ${isToday(dayPlan?.date)
                ? 'bg-secondary/5 dark:bg-secondary/10'
                : 'bg-gray-50 dark:bg-gray-900'
                }`}
            >
              <p className={`text-sm font-medium ${isToday(dayPlan?.date)
                ? 'text-secondary'
                : 'text-gray-700 dark:text-gray-300'
                }`}>
                {getDayName(dayPlan?.date)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate_(dayPlan?.date)}
              </p>
            </div>
          ))}
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {['breakfast', 'lunch', 'dinner'].map((mealType) => (
            <div key={mealType} className="grid grid-cols-8 divide-x divide-gray-200 dark:divide-gray-700">
              <div className="py-4 px-4 bg-gray-50 dark:bg-gray-900 flex items-center">
                <span className="text-gray-700 dark:text-gray-300 font-medium capitalize">
                  {mealType}
                </span>
              </div>

              {weeklyPlan.map((dayPlan: any, dayIndex) => {
                const mealsForType = dayPlan.meals[mealType as 'breakfast' | 'lunch' | 'dinner' | 'snacks'] as Meal | null | undefined;
                const mealCount = Array.isArray(mealsForType) ? mealsForType.length : (mealsForType ? 1 : 0);
                const meals: Meal[] = Array.isArray(mealsForType) ? mealsForType as Meal[] : [mealsForType as Meal];


                return (
                  <div key={dayIndex} className="p-2 min-h-24 relative">
                    {mealCount > 0 ? (
                      <div onClick={() => {
                        if (meals[0]) {
                          handleShowRecipe(meals[0]);
                        }
                        // openAddMealModal(dayPlan?.date, mealType, meals)
                      }} className="rounded-lg border cursor-pointer border-gray-200 dark:border-gray-700 overflow-hidden h-full hover:border-secondary transition-colors group">
                        {Array.isArray(mealsForType) ? (
                          <>
                            <div className="h-20 overflow-hidden relative">
                              <img
                                src={mealsForType[0].image}
                                alt={mealsForType[0].title}
                                className="w-full h-full object-cover"
                              />
                              {mealCount === 1 && <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveMeal(dayPlan?.date, mealType, mealsForType[0].title)
                                }}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} />
                              </button>}
                            </div>
                            <div className="p-2">
                              <p className="text-xs font-medium line-clamp-2 text-gray-800 dark:text-gray-200">
                                {mealsForType[0].title}
                                {mealCount > 1 && (
                                  <span className="ml-1 text-secondary">+{mealCount - 1} more</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {mealsForType[0].calories} kcal
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="h-20 overflow-hidden relative">
                              <img
                                src={mealsForType?.image || ''}
                                alt={mealsForType?.title || ''}
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => handleRemoveMeal(dayPlan?.date, mealType)}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <div className="p-2">
                              <p className="text-xs font-medium line-clamp-2 text-gray-800 dark:text-gray-200">
                                {mealsForType?.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {mealsForType?.calories ?? 0} kcal
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => openAddMealModal(dayPlan?.date, mealType)}
                        className="w-full h-full flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700 hover:border-secondary transition-colors"
                      >
                        <Plus size={18} className="text-gray-400 dark:text-gray-600" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="grid grid-cols-8 divide-x divide-gray-200 dark:divide-gray-700">
            <div className="py-3 px-4 bg-gray-100 dark:bg-gray-900 flex items-center">
              <span className="text-gray-700 dark:text-gray-300 font-medium">Total Calories</span>
            </div>

            {weeklyPlan.map((dayPlan, dayIndex) => {
              const totalCalories = calculateDailyCalories(dayPlan);
              return (
                <div
                  key={dayIndex}
                  className={`py-3 px-2 text-center ${totalCalories > 2000
                    ? 'bg-orange-50 dark:bg-orange-900/20'
                    : 'bg-gray-100 dark:bg-gray-900'
                    }`}
                >
                  <p className={`font-medium ${totalCalories > 2000
                    ? 'text-orange-700 dark:text-orange-400'
                    : 'text-gray-700 dark:text-gray-300'
                    }`}>
                    {totalCalories} kcal
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-6">
      {weeklyPlan.map((dayPlan, dayIndex) => (
        <div
          key={dayIndex}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div className={`px-6 py-4 ${isToday(dayPlan?.date)
            ? 'bg-secondary/5 dark:bg-secondary/10'
            : 'bg-gray-50 dark:bg-gray-900'
            }`}>
            <h3 className={`text-lg font-semibold ${isToday(dayPlan?.date)
              ? 'text-secondary'
              : 'text-gray-800 dark:text-white'
              }`}>
              {dayPlan?.date?.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {['breakfast', 'lunch', 'dinner'].map((mealType) => {
              const mealsForType = dayPlan.meals[mealType as 'breakfast' | 'lunch' | 'dinner' | 'snacks'] as Meal | null | undefined;
              const mealCount = Array.isArray(mealsForType) ? mealsForType.length : (mealsForType ? 1 : 0);

              return (
                <div key={mealType} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {mealType}
                    </h4>
                    {!mealCount && (
                      <button
                        onClick={() => openAddMealModal(dayPlan?.date, mealType)}
                        className="text-secondary hover:text-secondary/80 text-sm font-medium flex items-center"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Meal
                      </button>
                    )}
                  </div>

                  {mealCount > 0 ? (
                    <div className="space-y-4">
                      {Array.isArray(mealsForType) ? (
                        mealsForType.map((meal, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <img
                                src={meal.image}
                                alt={meal.title}
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div>
                                <h5 className="font-medium text-gray-800 dark:text-white">
                                  {meal.title}
                                </h5>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {meal.calories} kcal
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveMeal(dayPlan?.date, mealType)}
                              className="p-2 text-red-500 hover:text-red-600"
                            >
                              <X size={20} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <img
                              src={mealsForType?.image || ''}
                              alt={mealsForType?.title || ''}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div>
                              <h5 className="font-medium text-gray-800 dark:text-white">
                                {mealsForType?.title || ''}
                              </h5>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {mealsForType?.calories ?? 0} kcal
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMeal(dayPlan?.date, mealType)}
                            className="p-2 text-red-500 hover:text-red-600"
                          >
                            <X size={20} />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No meal planned
                    </p>
                  )}
                </div>
              );
            })}

            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Calories
                </span>
                <span className="font-medium text-gray-800 dark:text-white">
                  {calculateDailyCalories(dayPlan)} kcal
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Meal Planner</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Our unique meal planner ensures you know exactly what's on your plate and when!
          </p>
        </div>

        <div className="w-fit lg:flex grid items-center gap-3">
          <button
            data-tooltip="shopping"
            onClick={() => setShowMarketList(true)}
            className="flex items-center justify-center px-4 py-2 dark:bg-orchid-700 border border-gray-300 dark:border-orchid-700 rounded-lg text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-orchid-600"
          >
            <ShoppingBag size={18} className="mr-2" />
            Shopping List
          </button>
          <button
            data-tooltip="template"
            onClick={() => setShowTemplateModal(true)}
            className="flex items-center gap-2 px-4 py-2 justify-center border border-secondary-700 text-white hover:bg-gray-900 rounded-lg whitespace-nowrap"
          >
            <Settings size={18} />
            Template Builder
          </button>

        </div>
      </div>

      <div className="relative max-w-full mt-4 mb-4">
        <button
          onClick={() => window.open('https://ezyvibes.com/disclaimer', '_blank')}
          className="flex items-center justify-between w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-900 px-4 py-2 rounded-lg mb-4 transition-colors duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500"
        >
          <span className="truncate font-medium">
            ⚠️ Important:
            <span className="hidden sm:inline">
              &nbsp;Meal plans are based on your preferences. Double-check ingredients for allergens and dietary needs.
            </span>
          </span>
          <span className="flex items-center gap-1 text-sm text-yellow-800 group-hover:text-yellow-900 transition-colors">
            Read full disclaimer <ChevronRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      {mealTemplate.length < 1 ? (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-start">
            <Info className="text-blue-500 mt-1 mr-4 flex-shrink-0" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                Set Up Your Recipe Template
              </h3>
              <p className="text-blue-700 dark:text-blue-400 mt-2">
                Add 14 recipes from your cookbook to Week 1 and Week 2; and they'll seamlessly repeat in Week 3 and Week 4.
                Say goodbye to other midweek meal plans — Ezyvibes has your week covered, all 7 days!
              </p>
              <p className="text-blue-700 dark:text-blue-400 mt-2">
                Enjoy healthy, low-calorie recipes for weekdays and delightful takeaways for the weekend — everything you need for a diverse and delicious week.
              </p>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center"
              >
                <Settings size={18} className="mr-2" />
                Build Your Template
              </button>
            </div>
          </div>
        </div>
      ) : (
        weeklyPlan && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
              <Check size={24} className="text-green-600 dark:text-green-300" />
            </div>
            <div className="ml-4">
              <h3 className="text-md font-semibold text-green-800 dark:text-green-300">
                Your Meal Plan is Ready!
              </h3>
              <p className="text-green-700 text-sm dark:text-green-400">

                You've added your recipes to a 4-week plan, and we've automatically slotted them across breakfast, lunch, and dinner for the whole month.
                For the best variety, we recommend adding up to 21 meals. From here, your plan takes care of itself—no last-minute stress, just tasty, balanced meals ready when you are.
                Fancy a change? Shuffle, swap, or refresh anytime to keep things feeling fresh.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="md:flex justify-center items-center md:gap-4" data-tooltip="generate">
            <span className="text-lg font-medium text-gray-800 dark:text-white">
              {formatDate_(weeklyPlan[0]?.date)} - {formatDate_(weeklyPlan[6]?.date)}
            </span>
          </div>

          <button
            onClick={() => navigateWeek('next')}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex justify-between items-center w-full">
          {/* Left group */}
          <div className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('calendar')}
              disabled={isTabletOrMobile}
              className={`px-4 py-2 flex items-center ${viewMode === 'calendar'
                ? 'bg-secondary-700 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
              <CalendarIcon size={18} className="mr-2" />
              <span className='md:block hidden'>Calendar</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 flex items-center ${viewMode === 'list'
                ? 'bg-secondary-700 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
              <List size={18} className="mr-2" />
              <span className='md:block hidden'>List</span>
            </button>
          </div>

          {/* Right button */}
          <div className="flex gap-3">
            <div className="inline-flex items-center bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
              {(!weeklyPlan || mealTemplate.length < 1 ? <button
                onClick={() => setShowTemplateModal(true)}
                disabled={isGeneratingPlan}
                className={`px-4 py-2 flex items-center text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700}`}
              >
                <Shuffle size={18} className="mr-2" />
                <span className='md:block hidden'>{isGeneratingPlan ? 'Generating...' : 'Create'}</span>
              </button> : <button
                onClick={() => handleGenerateWeeklyPlan()}
                disabled={mealTemplate.length < 1 || isGeneratingPlan}
                className={`px-4 py-2 flex items-center bg-white text-black dark:text-black hover:bg-gray-900 dark:hover:bg-gray-900`}
              >
                <Shuffle size={18} className="mr-2" />
                <span className='md:block hidden'>{isGeneratingPlan ? 'Generating...' : 'Update'}</span>
              </button>)}
              <button
                onClick={() => handleClearPlan()}
                className={`px-4 py-2 flex items-center bg-red-600 text-white hover:bg-red-700 border border-red-200 dark:border-red-700`}
              >
                <Trash size={18} className="mr-2" />
                <span className='md:block hidden'>Clear</span>
              </button>
            </div>
          </div>

        </div>

      </div>

      {viewMode === 'calendar' ? renderCalendarView() : renderListView()}

      <Alert open={alertIsOpen} setOpen={() => setAlertIsOpen(!alertIsOpen)} onClick={() => setIsGeneratingPlan(true)} />

      {showAddMealModal && selectedDay && (
        <AddMealModal
          isOpen={showAddMealModal}
          onClose={() => setShowAddMealModal(false)}
          selectedDate={selectedDay}
          mealType={selectedMealType}
          onAddMeal={handleAddMeal}
          selected={selectedMeals}
        />
      )}

      {selectedRecipe && <RecipeModal
        recipe={selectedRecipe}
        onClose={() => setSelectedRecipe(null)}
      />}

      <TemplateBuilderModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        template={mealTemplate}
        onUpdateTemplate={setMealPlan}
      />

      <MarketListModal
        isOpen={showMarketList}
        onClose={() => setShowMarketList(false)}
        weeklyPlan={allPlans}
      />
    </div>
  );
};

export default MealPlanner;